<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    max-width="900px"
    transition="dialog-transition"
  >
    <v-card class="rounded-lg elevation-10">
      <!-- Header -->
      <v-card-title class="px-6 py-4 d-flex align-center justify-space-between">
        <h3 class="mb-0 font-weight-bold">Edit SKUs</h3>
        <v-btn icon @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider class="my-0"></v-divider>

      <!-- Body -->
      <v-card-text class="pa-6" style="max-height: 60vh">
        <template v-if="isSkusPresent">
          <div class="mb-4">
            <h5 class="text-h6 font-weight-medium mb-2">Selected SKUs</h5>
            <p class="text-body-2 text--secondary mb-0">
              Select the SKUs you want to include and adjust quantities
            </p>
          </div>

          <v-card outlined class="rounded-lg overflow-hidden">
            <v-data-table
              :headers="tableHeaders"
              :items="skus"
              hide-default-footer
              disable-pagination
              class="sku-table elevation-0"
            >
              <template v-slot:item.valid="{ item }">
                <v-checkbox
                  v-model="item.valid"
                  color="indigo"
                  hide-details
                  dense
                  class="mt-0 pt-0"
                ></v-checkbox>
              </template>

              <template v-slot:item.systemCode="{ item }">
                <div class="d-flex align-center">
                  <v-icon small color="grey darken-1" class="mr-2"
                    >mdi-barcode</v-icon
                  >
                  <span class="font-weight-medium">{{
                    item?.systemCode || item?.sku_code
                  }}</span>
                </div>
              </template>

              <template v-slot:item.quantity="{ item }">
                <v-text-field
                  v-model="item.quantity"
                  dense
                  :disabled="!item.valid"
                  outlined
                  type="number"
                  :min="0"
                  hide-details
                  class="quantity-input"
                  prepend-inner-icon="mdi-package-variant-closed"
                ></v-text-field>
              </template>
            </v-data-table>
          </v-card>
        </template>

        <template v-else>
          <v-alert type="info" outlined class="mb-0">
            <div class="d-flex align-center">
              <v-icon class="mr-3">mdi-information</v-icon>
              <span>No SKUs available for this order</span>
            </div>
          </v-alert>
        </template>
      </v-card-text>

      <v-divider></v-divider>

      <!-- Actions -->
      <v-card-actions class="px-6 py-4 d-flex justify-end">
        <v-btn
          text
          large
          color="grey darken-1"
          @click="dialog = false"
          class="mr-2"
        >
          Cancel
        </v-btn>
        <v-btn
          large
          depressed
          color="indigo"
          class="white--text px-8"
          @click="toggleModal"
        >
          <v-icon left>mdi-check</v-icon>
          {{ isSkusPresent ? "Update" : "Close" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// import ApiService from "@/core/services/api.service";
import Swal from "sweetalert2";
import { SET_INTERNAL_ORDER_SKUS } from "@/core/services/store/returned.module";
export default {
  name: "EditSkus",
  props: ["items"],
  data: () => ({
    dialog: false,
    skus: [],
    sku_search_text: null,
    suggestedItems: [],
    isLoading: false,
    selectedOrder: null,
    tableHeaders: [
      { text: "Select", value: "valid", sortable: false, width: "100px" },
      { text: "SKU code", value: "systemCode", sortable: false },
      { text: "Quantity", value: "quantity", sortable: false, width: "200px" },
    ],
  }),
  computed: {
    isSkusPresent: function () {
      return !!this.skus.length;
    },
  },
  watch: {
    items: {
      handler(newItems) {
        // Update skus when items prop changes and dialog is open
        if (this.dialog && this.selectedOrder) {
          const found = newItems.find(
            (item) => item.order_id === this.selectedOrder.order_id
          );
          if (found) {
            this.skus = found.skus.map((item) => ({
              ...item,
              valid: item.valid ?? true,
            }));
            this.$store.commit(SET_INTERNAL_ORDER_SKUS, this.skus);
          }
        }
      },
      deep: true,
    },
  },
  methods: {
    toggleModal(order) {
      if (!this.dialog) {
        this.selectedOrder = order;

        // Get SKUs from getRETURNSEditData (these will be checked by default)
        const editDataSkus = this.$store.getters.getRETURNSEditData?.skus || [];

        // Get SKUs from getRETURNSInternalOrdersSkus
        const internalOrderSkus =
          this.$store.getters.getRETURNSInternalOrderRelatedSkus || [];

        // Create a Map to store distinct SKUs by their code
        const skuMap = new Map();

        // First, add all SKUs from internalOrderSkus (unchecked by default)
        internalOrderSkus.forEach((sku) => {
          const skuCode = sku.systemCode || sku.sku_code;
          if (skuCode) {
            skuMap.set(skuCode, {
              ...sku,
              valid: false, // Unchecked by default
            });
          }
        });

        // Then, add/overwrite with SKUs from editDataSkus (checked by default)
        editDataSkus.forEach((sku) => {
          const skuCode = sku.systemCode || sku.sku_code;
          if (skuCode) {
            skuMap.set(skuCode, {
              ...sku,
              valid: true, // Checked by default (overwrites if already exists)
            });
          }
        });

        // Convert Map values to array
        this.skus = Array.from(skuMap.values());

        this.$store.commit(SET_INTERNAL_ORDER_SKUS, this.skus);
      } else if (this.dialog) {
        // check if atleast one valid sku present
        const isValid = this.skus.some((item) => item.valid);
        if (this.isSkusPresent && !isValid) {
          Swal.fire({
            title: "Warning",
            text: "Please, select a sku!",
            icon: "warning",
            showConfirmButton: true,
            timer: 2500,
          });
          return;
        }
        //check if any order has invalid qty
        const isAnyOrderMissingQty = this.skus.some(
          (item) => item.quantity === ""
        );
        if (this.isSkusPresent && isAnyOrderMissingQty) {
          Swal.fire({
            title: "Warning",
            text: "Enter valid SKU quantity",
            icon: "warning",
            showConfirmButton: true,
            timer: 2500,
          });
          return;
        }
        const anyZeroQty = this.skus.some(
          (item) => Number(item.quantity) === 0 && item.valid
        );
        const anyNegativeQty = this.skus.some(
          (item) => Number(item.quantity) < 0 && item.valid
        );
        if (anyNegativeQty) {
          Swal.fire({
            title: "Warning",
            text: "SKU quantity cannot be negative",
            icon: "warning",
            showConfirmButton: true,
            timer: 2500,
          });
          return;
        }
        //check atleast one sku which has qty > 0
        const hasValidQty = this.skus.some(
          (item) => Number(item.quantity) && item.valid
        );

        if (anyZeroQty) {
          Swal.fire({
            title: "Warning",
            text: "No SKU should have zero quantity",
            icon: "warning",
            showConfirmButton: true,
            timer: 2500,
          });
          return;
        }

        if (this.isSkusPresent && !hasValidQty) {
          Swal.fire({
            title: "Warning",
            text: "Order should contain atleast 1 SKU",
            icon: "warning",
            showConfirmButton: true,
            timer: 2500,
          });
          return;
        }

        // Validate quantities against original order SKUs
        const realOrderSkus =
          this.items.find(
            (item) => item.order_id === this.selectedOrder.order_id
          )?.skus || [];

        let hasValidQty2 = true;
        let exceededSkuCode = "";

        // Check only selected (valid) SKUs
        this.skus.forEach((sku) => {
          if (sku.valid) {
            const skuCode = sku.systemCode || sku.sku_code;
            // Find the corresponding SKU in realOrderSkus by matching SKU code
            const originalSku = realOrderSkus.find(
              (realSku) => (realSku.systemCode || realSku.sku_code) === skuCode
            );

            if (
              originalSku &&
              Number(sku.quantity) > Number(originalSku.quantity)
            ) {
              hasValidQty2 = false;
              exceededSkuCode = skuCode;
            }
          }
        });

        if (!hasValidQty2) {
          Swal.fire({
            title: "Warning",
            text: `Quantity for ${exceededSkuCode} exceeds original order quantity`,
            icon: "warning",
            showConfirmButton: true,
            timer: 2500,
          });
          return;
        }

        // Only send selected (valid) SKUs
        const selectedSkus = this.skus.filter((item) => item.valid);
        this.$store.commit(SET_INTERNAL_ORDER_SKUS, selectedSkus);
        this.$emit("updateOrderDetails", selectedSkus);
      }
      this.dialog = !this.dialog;
    },
    // onChangeSkus() {
    //   this.$store.commit(SET_INTERNAL_ORDER_SKUS, this.skus);
    // },
  },
};
</script>

<style scoped>
.sku-table >>> .v-data-table-header {
  background-color: #f5f7fa;
}

.sku-table >>> .v-data-table-header th {
  font-weight: 600 !important;
  font-size: 14px !important;
  color: #4a5568 !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.sku-table >>> tbody tr:hover {
  background-color: #f8f9fa !important;
}

.sku-table >>> tbody td {
  padding: 16px 16px !important;
  height: auto !important;
}

.quantity-input {
  max-width: 150px;
}

.quantity-input >>> .v-input__control {
  min-height: 40px !important;
}

.quantity-input >>> input {
  font-weight: 500;
  font-size: 14px;
}

.v-card {
  overflow: hidden;
}

/* Smooth transitions */
.v-dialog {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
